// Line Items — a Line items field shown as a PLAN (definition.view.kind === 'plan'). Every line is a
// planned day, in the order the lines were added: the LAST line is the plan, each earlier one a day
// the plan moved from. Drawn as its moves — the plan now, large, red when it has passed; under it the
// days it moved from, how far each move pushed it and why — with "Move the plan" adding a line (the
// day and why). With view.followsDue the work item's Due date moves to the new day (the normal field
// update, as the person). The last move can be taken back (its line removed; the Due date goes back
// with it). The table stays one click away ("Show as a table").

import React, { useState } from 'react';
import api, { extractErrorMessage } from '@app/services/api';
import lineItemsApi from '@modules/database/line-items/frontend/api';
import DateEditor from '@modules/database/work-items/frontend/editors/DateEditor';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayOf = (iso) => { const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? Math.round(Date.UTC(+m[1], +m[2] - 1, +m[3]) / 86400000) : null; };
const fmt = (iso) => { const d = dayOf(iso); if (d == null) return '—'; const x = new Date(d * 86400000); return `${x.getUTCDate()} ${MONTHS[x.getUTCMonth()]} ${String(x.getUTCFullYear()).slice(2)}`; };
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const days = (n) => `${Math.abs(n)} day${Math.abs(n) === 1 ? '' : 's'}`;

export default function PlanView({ issueKey, fieldId, state, onChanged, children }) {
  const def = state.definition;
  const v = def.view;
  const lines = (state.lines || []).filter((l) => dayOf(l.vals && l.vals[v.date]) != null);
  const plan = lines[lines.length - 1] || null;
  const moves = lines.slice(0, -1);
  const today = dayOf(todayIso());
  const late = plan && dayOf(plan.vals[v.date]) < today;
  const canEdit = state.canEdit && !state.locked && !(state.document && state.document.locked);
  const [moving, setMoving] = useState(false);
  const [day, setDay] = useState('');
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [asTable, setAsTable] = useState(false);

  const followDue = async (iso) => { if (v.followsDue && iso) await api.updateIssue(issueKey, { duedate: iso }); };
  const move = async () => {
    if (!day) return;
    setBusy(true); setError(null);
    try {
      await lineItemsApi.addLine(issueKey, fieldId, { vals: { [v.date]: day, ...(v.note ? { [v.note]: why.trim() } : {}) } });
      await followDue(day);
      setMoving(false); setDay(''); setWhy('');
    } catch (e) { setError(extractErrorMessage ? extractErrorMessage(e) : (e && e.message) || 'The plan could not be moved.'); }
    setBusy(false); onChanged();
  };
  const takeBack = async () => {
    if (!plan || !moves.length) return;
    if (!window.confirm(`Take back the last move — back to ${fmt(moves[moves.length - 1].vals[v.date])}?`)) return;
    setBusy(true); setError(null);
    try { await lineItemsApi.deleteLine(plan.id); await followDue(moves[moves.length - 1].vals[v.date]); }
    catch (e) { setError(extractErrorMessage ? extractErrorMessage(e) : (e && e.message) || 'Not taken back.'); }
    setBusy(false); onChanged();
  };

  if (asTable) {
    return (
      <div>
        {children}
        <button type="button" className="li-plan-link" onClick={() => setAsTable(false)}>Show as the plan</button>
      </div>
    );
  }
  return (
    <div className="li-plan">
      {error && <div className="li-error">{error}</div>}
      {plan ? (
        <div className={`li-plan-now${late ? ' li-plan-late' : ''}`}>
          <div className="li-plan-eyebrow">{late ? 'The plan — passed' : 'The plan'}</div>
          <div className="li-plan-day">{fmt(plan.vals[v.date])}</div>
          <div className="li-plan-sub">
            {moves.length ? `moved ${moves.length}× — first planned ${fmt(moves[0].vals[v.date])} (${dayOf(plan.vals[v.date]) - dayOf(moves[0].vals[v.date]) >= 0 ? '+' : '−'}${days(dayOf(plan.vals[v.date]) - dayOf(moves[0].vals[v.date]))})` : 'as first planned'}
            {late ? ` · ${days(today - dayOf(plan.vals[v.date]))} ago` : dayOf(plan.vals[v.date]) === today ? ' · today' : ` · in ${days(dayOf(plan.vals[v.date]) - today)}`}
          </div>
        </div>
      ) : <div className="li-muted li-note">Not planned yet.</div>}

      {lines.length > 0 && (
        <ol className="li-plan-steps" aria-label="The plan's moves">
          {lines.map((l, i) => {
            const d = dayOf(l.vals[v.date]); const prev = i ? dayOf(lines[i - 1].vals[v.date]) : null;
            const current = i === lines.length - 1;
            return (
              <li key={l.id} className={current ? 'li-plan-step li-plan-current' : 'li-plan-step'}>
                <span className="li-plan-dot" aria-hidden="true" />
                <span className="li-plan-when">{fmt(l.vals[v.date])}</span>
                {prev != null ? <span className={`li-plan-delta${d - prev > 0 ? ' li-plan-later' : ''}`}>{d - prev >= 0 ? '+' : '−'}{days(d - prev)}</span> : <span className="li-plan-delta">first plan</span>}
                {v.note && l.vals[v.note] ? <span className="li-plan-why">{l.vals[v.note]}</span> : null}
                {current ? <span className="li-plan-tag">the plan now</span> : null}
              </li>
            );
          })}
        </ol>
      )}

      {canEdit && (moving ? (
        <div className="li-plan-move">
          <div className="li-plan-eyebrow">Move the plan to</div>
          <DateEditor value={day} onAutoSave={(x) => setDay(x || '')} />
          {v.note ? <input className="li-plan-why-input" value={why} onChange={(e) => setWhy(e.target.value)} placeholder="Why it moved (optional)" aria-label="Why it moved" /> : null}
          <div className="li-plan-bar">
            <button type="button" className="zsc-btn zsc-btn-primary" disabled={!day || busy} onClick={move}>{busy ? 'Moving…' : day ? `Move to ${fmt(day)}` : 'Pick the new day'}</button>
            <button type="button" className="zsc-btn zsc-btn-secondary" onClick={() => { setMoving(false); setDay(''); setWhy(''); }}>Cancel</button>
            {v.followsDue ? <span className="li-muted">The Due date moves with it.</span> : null}
          </div>
        </div>
      ) : (
        <div className="li-plan-bar">
          <button type="button" className="zsc-btn zsc-btn-secondary" onClick={() => setMoving(true)} disabled={busy}>{plan ? 'Move the plan' : 'Plan it'}</button>
          {moves.length > 0 && <button type="button" className="li-plan-link" onClick={takeBack} disabled={busy}>Take back the last move</button>}
          <span style={{ flex: 1 }} />
          <button type="button" className="li-plan-link" onClick={() => setAsTable(true)}>Show as a table</button>
        </div>
      ))}
      {!canEdit && <div className="li-plan-bar"><span style={{ flex: 1 }} /><button type="button" className="li-plan-link" onClick={() => setAsTable(true)}>Show as a table</button></div>}
    </div>
  );
}
